import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import PeriodicAuditForm from 'routes/PerodicAudit/components/PeriodicAuditForm';
import { Ref } from 'routes/PerodicAudit/components/PeriodicAuditForm/PeriodicAuditForm';
import { RootState } from 'data/root.reducers';
import * as selectors from 'data/periodicAudit/periodicAudit.selectors';
import * as actions from 'data/periodicAudit/periodicAudit.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { PeriodicAudit } from 'data/periodicAudit/periodicAudit.types';
import { periodicAuditTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/PeriodicAudit.adapter';
import { getAuditDetailsToSubmit } from '../../utils/periodicAuditDataToSubmit';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  periodicAuditTemplateDef: adaptTemplateVisibilityMode(periodicAuditTemplate, 'edit'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  selectedPeriodicAFAudit: selectors.getSelectedPeriodicAudit(state)[0],
  periodicAudit: selectors.getPeriodicAudit(state),
  originalSelectedPeriodicAFAudit: selectors.getSelectedPeriodicAudit(state)[0],
});

const mapDispatch = {
  updatePeriodicAudit: actions.updatePeriodicAudit,
  goToPeriodicAuditView: actions.goToPeriodicAuditView,
  getPeriodicAudit: actions.getPeriodicAudit,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const PeriodicAuditEdit = ({
  history,
  selectedPeriodicAFAudit,
  originalSelectedPeriodicAFAudit,
  periodicAudit,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  periodicAuditTemplateDef,
  updatePeriodicAudit,
  goToPeriodicAuditView,
  getPeriodicAudit,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<PeriodicAudit>>({});
  const [template, setTemplate] = useState(periodicAuditTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getPeriodicAudit(deliveryFunctionId);
  }, [deliveryFunctionId, getPeriodicAudit]);

  useEffect(() => {
    if (periodicAudit != null && template != null) {
      const clonedTemplate = cloneDeep(template);
      setFormData(periodicAudit);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate.key = Date.now().toString();

      if (clonedTemplate.main) {
        const days = clonedTemplate.main.fields;
        const period = periodicAudit.CAUDPERIOD;

        days.monday.initial = Number(period.charAt(0));
        days.tuesday.initial = Number(period.charAt(1));
        days.wednesday.initial = Number(period.charAt(2));
        days.thursday.initial = Number(period.charAt(3));
        days.friday.initial = Number(period.charAt(4));
        days.saturday.initial = Number(period.charAt(5));
        days.sunday.initial = Number(period.charAt(6));
      }

      setTemplate(clonedTemplate);
    }
  }, [
    periodicAudit,
  ]);

  const getCreateDistributorActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const periodicAuditData = formRef.current.getValues();

    if (periodicAudit) {
      periodicAuditData.IRICLEAR = periodicAudit.IRICLEAR;
      periodicAuditData.IRIEXP = periodicAudit.IRIEXP;
      periodicAuditData.IRIEXPMNT = periodicAudit.IRIEXPMNT;
      periodicAuditData.IRISIZE = periodicAudit.IRISIZE;
    }

    periodicAuditData.monday = periodicAuditData.monday ? '1' : '0';
    periodicAuditData.tuesday = periodicAuditData.tuesday ? '1' : '0';
    periodicAuditData.wednesday = periodicAuditData.wednesday ? '1' : '0';
    periodicAuditData.thursday = periodicAuditData.thursday ? '1' : '0';
    periodicAuditData.friday = periodicAuditData.friday ? '1' : '0';
    periodicAuditData.saturday = periodicAuditData.saturday ? '1' : '0';
    periodicAuditData.sunday = periodicAuditData.sunday ? '1' : '0';

    periodicAuditData.CAUDPERIOD = periodicAuditData.monday
      + periodicAuditData.tuesday
      + periodicAuditData.wednesday
      + periodicAuditData.thursday
      + periodicAuditData.friday
      + periodicAuditData.saturday
      + periodicAuditData.sunday;

    const payload = {
      data: periodicAuditData,
      auditDetails: getAuditDetailsToSubmit(
        periodicAuditData,
        originalSelectedPeriodicAFAudit,
        isAuditEnabled,
        userName,
        userId,
        'UPDATE',
      ),
    };
    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateDistributorActionPayload();

    if (payload) {
      updatePeriodicAudit(
        deliveryFunctionId, payload, history,
      );
    }
  };

  const handleCancel = () => goToPeriodicAuditView(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as PeriodicAudit);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <PeriodicAuditForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (periodicAudit == null) {
    return <Fragment />;
  }
  console.log(periodicAudit)

  return (
    <div data-testid="PeriodicAuditEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('periodicAFAudit.edit.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(PeriodicAuditEdit));
